/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { UnitStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitStatusHistoryEntryProps {
  id: string;
  startTime: Date;
  endTime: Date;
  status: UnitStatus;
  unitId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class UnitStatusHistoryEntryRecord extends Record<UnitStatusHistoryEntryProps>({
  id: null as any,
  startTime: null as any,
  endTime: null as any,
  status: null as any,
  unitId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    startTime: string | number | Date;
    endTime: string | number | Date;
    status: UnitStatus;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): UnitStatusHistoryEntryRecord {
    if (json == null) {
      throw new Error('UnitStatusHistoryEntryRecord.fromJS: parameter cannot be null');
    }
    return new UnitStatusHistoryEntryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitStatusHistoryEntryJson.id',
      ),
      startTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'startTime', '0001-01-01T00:00:00.00Z'),
          'UnitStatusHistoryEntryJson.startTime',
        ),
      ),
      endTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'endTime', '0001-01-01T00:00:00.00Z'),
          'UnitStatusHistoryEntryJson.endTime',
        ),
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', UnitStatus.Available),
        'UnitStatusHistoryEntryJson.status',
      ),
      unitId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
        'UnitStatusHistoryEntryJson.unitId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'UnitStatusHistoryEntryJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    startTime: string | number | Date;
    endTime: string | number | Date;
    status: UnitStatus;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [UnitStatusHistoryEntryRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitStatusHistoryEntryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitStatusHistoryEntryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitStatusHistoryEntryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      startTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'startTime', '0001-01-01T00:00:00.00Z'),
              'UnitStatusHistoryEntryJson.startTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      endTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'endTime', '0001-01-01T00:00:00.00Z'),
              'UnitStatusHistoryEntryJson.endTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', UnitStatus.Available),
            'UnitStatusHistoryEntryJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
            'UnitStatusHistoryEntryJson.unitId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'UnitStatusHistoryEntryJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: UnitStatusHistoryEntryProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitStatusHistoryEntryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitStatusHistoryEntryViewModelBase implements UnitStatusHistoryEntryProps {
  protected readonly _props: UnitStatusHistoryEntryProps;
  private _json?: UnitStatusHistoryEntryProps;

  get id(): string {
    return this._props.id;
  }
  get startTime(): Date {
    return this._props.startTime;
  }
  get endTime(): Date {
    return this._props.endTime;
  }
  get status(): UnitStatus {
    return this._props.status;
  }
  get unitId(): string {
    return this._props.unitId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAvailable() {
    return this._props.status === UnitStatus.Available;
  }
  public get isCommitted() {
    return this._props.status === UnitStatus.Committed;
  }
  public get isOnStandby() {
    return this._props.status === UnitStatus.OnStandby;
  }
  public get isOutOfService() {
    return this._props.status === UnitStatus.OutOfService;
  }
  public get isPartiallyAvailable() {
    return this._props.status === UnitStatus.PartiallyAvailable;
  }

  public isAnyStatus(...unitStatus: UnitStatus[]) {
    return unitStatus.includes(this._props.status);
  }

  constructor(props: UnitStatusHistoryEntryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        startTime: this._props.startTime,
        endTime: this._props.endTime,
        status: this._props.status,
        unitId: this._props.unitId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllUnitStatusHistoryEntryEntities = (state: AppState) =>
  state.entityStores.unitStatusHistoryEntry.entities;

export const selectUnitStatusHistoryEntryById = (id: string) => (state: AppState) =>
  state.entityStores.unitStatusHistoryEntry.entities.get(id);

export const selectUnitStatusHistoryEntryMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.unitStatusHistoryEntry.meta.get(id);

export const selectUnitStatusHistoryEntryAndMetadataById = (id: string) =>
  createSelector(
    selectUnitStatusHistoryEntryById(id),
    selectUnitStatusHistoryEntryMetadataById(id),
    (unitStatusHistoryEntry, meta) => ({ unitStatusHistoryEntry, meta }),
  );

export function selectUnitStatusHistoryEntryEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllUnitStatusHistoryEntryEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, UnitStatusHistoryEntryRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectUnitStatusHistoryEntryEntitiesByStartTime(startTime: Date) {
  return createSelector(selectAllUnitStatusHistoryEntryEntities, (entities) =>
    entities.filter((e) => e.startTime === startTime).valueSeq(),
  );
}

export function selectUnitStatusHistoryEntryEntitiesByEndTime(endTime: Date) {
  return createSelector(selectAllUnitStatusHistoryEntryEntities, (entities) =>
    entities.filter((e) => e.endTime === endTime).valueSeq(),
  );
}

export function selectUnitStatusHistoryEntryEntitiesByStatus(status: UnitStatus) {
  return createSelector(selectAllUnitStatusHistoryEntryEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectUnitStatusHistoryEntryEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllUnitStatusHistoryEntryEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectUnitStatusHistoryEntryEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllUnitStatusHistoryEntryEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
